import { Components, tabClasses, tabsClasses, Theme } from '@mui/material'
import { secondaryGhostIconButtonStyle } from './MuiIconButton'

// Rounded pill padding, less the label's own optical padding on any side an icon stands in on
// (see the trims on MuiTab).
const pillPaddingLeft = (theme: Theme, factor: number) => `calc(${theme.spacing(factor)} - var(--omnea-Tab-leadingTextTrim, 0px))`
const pillPaddingRight = (theme: Theme, factor: number) =>
  `calc(${theme.spacing(factor)} - var(--omnea-Tab-trailingTextTrim, 0px))`

const SCROLL_FADE_SIZE = '16px'
// The overlaid control (28px) plus its clearance from the strip. Labels are masked out entirely
// across this band, so a chevron never lands on top of one.
const SCROLL_CONTROL_BAND = '36px'
const SCROLL_LEAD_IN = `calc(${SCROLL_CONTROL_BAND} + ${SCROLL_FADE_SIZE})`

declare module '@mui/material/Tabs' {
  interface TabsOwnProps {
    shape?: 'line' | 'rounded'
    /**
     * Allows the tab list to wrap onto multiple rows. Prefer a scrollable strip with
     * `scrollButtons="auto"`; use wrapping only for exceptional dense filter navigation.
     * Pair with `variant="standard"`.
     */
    wrap?: boolean
  }
}

export const MuiTabs: Components<Omit<Theme, 'components'>>['MuiTabs'] = {
  defaultProps: {
    variant: 'scrollable',
    scrollButtons: false,
    shape: 'line',
    slotProps: {
      root: () => ({
        // Don't leak our custom props to the DOM as invalid attributes
        shape: undefined,
        wrap: undefined,
      }),
    },
  },
  variants: [
    {
      props: { shape: 'rounded' },
      style: ({ theme }) => ({
        alignItems: 'center',
        backgroundImage: 'none',
        '& .MuiTabs-list': {
          // Row gap only: it spaces stacked pills when vertical and is inert when horizontal, where
          // the spacing comes from tab padding instead (see the pill below).
          rowGap: theme.spacing(2),
        },
        '& .MuiTabs-indicator': {
          display: 'none',
        },
        '& .MuiTab-root': {
          // Figma puts 2.5 on the pill and another 0.5 on the text inside it; MUI renders the label
          // straight into the tab with nothing to pad separately, so the two arrive as one 3.
          paddingLeft: pillPaddingLeft(theme, 3),
          paddingRight: pillPaddingRight(theme, 3),
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
          // We don't want the minHeight from lines shape, and a pill hugs its label rather than
          // taking Tab's 90px floor — the design's shortest trigger is narrower than that floor.
          minHeight: 'unset',
          minWidth: 'unset',
          '::before': {
            inset: 0,
            borderRadius: theme.shape['radius-full'],
            borderStyle: 'solid',
            borderWidth: theme.strokeWidth.default,
            borderColor: theme.palette.border.strong,
          },
          '&.Mui-selected::before': {
            backgroundColor: theme.palette.bg.interactive.active,
          },
        },
        // Horizontally, the space between pills is inset into the pill and carried by the tab's
        // padding rather than a list `gap`: MUI sizes a scroll-button click by summing tab
        // clientWidths, which excludes gaps, so a gap leaves a click short of the end. Only sides
        // with a neighbour to clear are inset, so the outer pills stay flush with the strip.
        [`&:not(.${tabsClasses.vertical}) .MuiTab-root`]: {
          // 3 of pill padding plus the 1 the pill is inset by to clear its neighbour
          paddingLeft: pillPaddingLeft(theme, 4),
          paddingRight: pillPaddingRight(theme, 4),
          '::before': {
            inset: `0 ${theme.spacing(1)}`,
          },
          '&:first-child': {
            paddingLeft: pillPaddingLeft(theme, 3),
            '::before': {
              left: 0,
            },
          },
          '&:last-child': {
            paddingRight: pillPaddingRight(theme, 3),
            '::before': {
              right: 0,
            },
          },
        },
      }),
    },
    {
      props: { wrap: true },
      style: ({ theme }) => ({
        // Keep the first row aligned with adjacent actions instead of centring it in Tabs' 40px
        // minimum height. This continues to top-align the actions when the list gains more rows.
        alignItems: 'flex-start',
        '& .MuiTabs-list': {
          flexWrap: 'wrap',
          columnGap: theme.spacing(2),
        },
        // Scrolling tabs carry spacing inside each tab because MUI's scroll calculations do not
        // include flex gaps. Wrapped tabs do not scroll, so use a real gap and remove those insets.
        // Every wrapped row can then start flush with the container, not just the first row.
        [`&:not(.${tabsClasses.vertical}) .MuiTab-root`]: {
          paddingLeft: pillPaddingLeft(theme, 3),
          paddingRight: pillPaddingRight(theme, 3),
          '::before': {
            left: 0,
            right: 0,
          },
        },
      }),
    },
  ],
  styleOverrides: {
    root: ({ theme }) => ({
      minHeight: 40,

      flexShrink: 1,
      minWidth: 0,
      maxWidth: '100%',
      width: '100%',
      overflow: 'hidden',
      position: 'relative',

      // The rule spans the root, not the list, so it runs behind the scroll buttons. It's the root's
      // own background because that paints inside the padding box beneath every child, leaving the
      // indicator over it: `overflow: hidden` clips a hairline pseudo-element on the edge, and a
      // border would sit below the indicator instead. The axis is a custom property so the vertical
      // override can't outweigh the flat `backgroundImage: none` the rounded shape drops it with.
      '--omnea-Tabs-ruleAxis': 'to top',
      backgroundImage: `linear-gradient(var(--omnea-Tabs-ruleAxis), ${theme.palette.border.subtle} ${theme.strokeWidth.default}, transparent ${theme.strokeWidth.default})`,
      // MUI applies the scrollButtons slot as a descendant selector, so it can't see the root's
      // orientation class — anything orientation-dependent has to be written from here.
      [`&:not(.${tabsClasses.vertical}) .${tabsClasses.scrollButtons}`]: {
        top: '50%',
        transform: 'translateY(-50%)',
        '&:first-child': {
          left: 0,
        },
        '&:last-child': {
          right: 0,
        },
      },
      [`&.${tabsClasses.vertical}`]: {
        '--omnea-Tabs-ruleAxis': 'to right',
        [`& .${tabsClasses.scrollButtons}`]: {
          // This inset is Tab's hover overlay inset, so the chevron shares a left edge with it
          left: theme.spacing(1),
          '&:first-child': {
            top: 0,
          },
          '&:last-child': {
            bottom: 0,
          },
        },
      },
    }),
    indicator: ({ theme }) => ({
      backgroundColor: theme.palette.border.inverted,
      height: theme.strokeWidth.strongest,

      // Switch indicator to left
      '.MuiTabs-vertical &': {
        left: 0,
        right: 'unset',
      },
    }),
    scrollButtons: ({ theme }) => ({
      ...secondaryGhostIconButtonStyle(theme),
      // MUI fades the button to 0.8
      opacity: 1,
      width: 'auto',
      height: 'auto',
      // Overlaid rather than in flow: in flow, a control appearing or leaving hands its width back
      // to the scroller and shifts every tab sideways mid-scroll.
      position: 'absolute',
      zIndex: 1,
      // Matches IconButton size="small"
      borderRadius: theme.shape['radius-md'],
      padding: theme.spacing(1),
      // A control for a direction you can't scroll is hidden, not dimmed — the shared ghost style
      // would otherwise dim it. `visibility` also stops it swallowing clicks meant for the tab it
      // sits over, which `opacity: 0` would not.
      '&.Mui-disabled': {
        visibility: 'hidden',
      },
    }),
    scroller: {
      // Scroll buttons are only rendered while the strip overflows, and lose Mui-disabled on the
      // side that can still scroll, so their state is the only signal for which edge to lead in.
      '--omnea-Tabs-leadIn': '0px',
      '--omnea-Tabs-leadOut': '0px',
      '--omnea-Tabs-leadAxis': 'to right',
      // A mask reads alpha only, so currentColor is just "opaque" here — never a rendered colour.
      // Each side holds fully transparent under the control, then ramps in over the fade; max()
      // keeps the stops ordered when a side has no lead-in at all.
      maskImage: `linear-gradient(var(--omnea-Tabs-leadAxis), transparent 0, transparent max(0px, calc(var(--omnea-Tabs-leadIn) - ${SCROLL_FADE_SIZE})), currentColor var(--omnea-Tabs-leadIn), currentColor calc(100% - var(--omnea-Tabs-leadOut)), transparent calc(100% - max(0px, calc(var(--omnea-Tabs-leadOut) - ${SCROLL_FADE_SIZE}))), transparent 100%)`,
      // Read by patches/@mui__material@7.1.0.patch when MUI scrolls the selected tab into view
      scrollPaddingLeft: 'var(--omnea-Tabs-leadIn)',
      scrollPaddingRight: 'var(--omnea-Tabs-leadOut)',
      [`.${tabsClasses.vertical} &`]: {
        '--omnea-Tabs-leadAxis': 'to bottom',
        scrollPaddingLeft: 0,
        scrollPaddingRight: 0,
        scrollPaddingTop: 'var(--omnea-Tabs-leadIn)',
        scrollPaddingBottom: 'var(--omnea-Tabs-leadOut)',
      },
      [`.${tabsClasses.scrollButtons}:not(.Mui-disabled) ~ &`]: {
        '--omnea-Tabs-leadIn': SCROLL_LEAD_IN,
      },
      [`.${tabsClasses.root}:has(& ~ .${tabsClasses.scrollButtons}:not(.Mui-disabled)) &`]: {
        '--omnea-Tabs-leadOut': SCROLL_LEAD_IN,
      },
    },
  },
}

export const MuiTab: Components<Omit<Theme, 'components'>>['MuiTab'] = {
  defaultProps: {
    disableRipple: true,
    iconPosition: 'start',
  },
  variants: [
    // Designs give the label a little optical padding of its own on top of the surrounding padding.
    // An icon takes the label's place at one edge without carrying it, so the side an icon sits on
    // reports how much to trim. iconPosition alone isn't enough — it defaults to start whether or
    // not there is an icon — hence the labelIcon guard, which means icon and label both present.
    {
      props: { iconPosition: 'start' },
      style: ({ theme }) => ({
        [`&.${tabClasses.labelIcon}`]: {
          '--omnea-Tab-leadingTextTrim': theme.spacing(0.5),
        },
      }),
    },
    {
      props: { iconPosition: 'end' },
      style: ({ theme }) => ({
        [`&.${tabClasses.labelIcon}`]: {
          '--omnea-Tab-trailingTextTrim': theme.spacing(0.5),
        },
      }),
    },
  ],
  styleOverrides: {
    root: ({ theme }) => ({
      minHeight: 40,
      ...theme.typography['body-medium'],
      textTransform: 'none',
      color: theme.palette.text.muted,
      '&.Mui-selected, &:hover': {
        color: theme.palette.text.default,
      },
      // Hack as 3.5 isn't allowed, but designs have 1 + 2.5 to support the hover overlay
      paddingLeft: `calc(${theme.spacing(1)} + ${theme.spacing(2.5)})`,
      paddingRight: `calc(${theme.spacing(1)} + ${theme.spacing(2.5)})`,
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(2),

      gap: theme.spacing(1.5),

      '.MuiTabs-vertical &': {
        justifyContent: 'flex-start',
        alignSelf: 'flex-start',
        minWidth: 0,
      },
      '.MuiTabs-scrollableX &': {
        whiteSpace: 'nowrap',
        maxWidth: 'none',
      },
      '::before': {
        content: '""',
        position: 'absolute',
        inset: `${theme.spacing(1)} ${theme.spacing(1)}`,
        borderRadius: theme.shape['radius-md'],
        backgroundColor: theme.palette.bg.invisible,
        zIndex: 0,
      },
      '&:hover::before': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },
    }),
    icon: () => ({
      fontSize: 18,
      // We use gap to space the icon and label, not margin
      margin: 0,
    }),
  },
}
